import React from 'react';
import Icon from 'components/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import AppTooltip from 'components/Tooltip';

const yieldData = [
  { metal: 'Cobre', estimado: 92, real: 89.5 },
  { metal: 'Aluminio', estimado: 85, real: 87.2 },
  { metal: 'Plomo', estimado: 78, real: 75.8 },
  { metal: 'Zinc', estimado: 88, real: 88.9 },
  { metal: 'Níquel', estimado: 94, real: 91.3 },
];

const marginData = [
  { mes: 'Oct', estimado: 12.4, real: 11.8 },
  { mes: 'Nov', estimado: 13.1, real: 13.6 },
  { mes: 'Dic', estimado: 11.9, real: 10.2 },
  { mes: 'Ene', estimado: 14.2, real: 14.8 },
  { mes: 'Feb', estimado: 12.8, real: 12.1 },
  { mes: 'Mar', estimado: 13.5, real: 13.9 },
];

const KPICard = ({ label, value, trend, trendLabel, icon, color, tooltip }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4">
    <div className="flex items-start justify-between">
      <div>
        <AppTooltip description={tooltip}>
          <p className="text-xs text-slate-500 mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>{label}</p>
        </AppTooltip>
        <p className="text-xl font-bold text-slate-800 font-mono">{value}</p>
      </div>
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon name={icon} size={18} color="currentColor" strokeWidth={2} />
      </div>
    </div>
    {trend !== undefined && (
      <div className={`flex items-center gap-1 mt-2 text-xs ${trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}
        style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
        <Icon name={trend >= 0 ? 'TrendingUp' : 'TrendingDown'} size={12} color="currentColor" />
        <span>{trend >= 0 ? '+' : ''}{trend}% {trendLabel}</span>
      </div>
    )}
  </div>
);

export default function PerformanceAnalytics() {
  return (
    <div className="space-y-5">
      <h2 className="text-base font-semibold text-slate-800" style={{ fontFamily: '"Outfit", sans-serif' }}>
        Análisis de Rendimiento
      </h2>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPICard label="Yield Promedio Real" value="89.3%" trend={-1.2} trendLabel="vs estimado" icon="Target" color="bg-blue-50 text-blue-600" tooltip="Promedio del rendimiento real obtenido en todos los lotes procesados. Compara el metal puro recuperado vs el peso bruto inicial." />
        <KPICard label="Margen Realizado" value="12.8%" trend={0.4} trendLabel="vs mes anterior" icon="TrendingUp" color="bg-emerald-50 text-emerald-600" tooltip="Margen de beneficio promedio efectivamente obtenido en las ventas cerradas del período actual." />
        <KPICard label="Tiempo Rotación" value="18.4d" trend={-2.1} trendLabel="vs estimado" icon="Clock" color="bg-amber-50 text-amber-600" tooltip="Tiempo promedio en días que tarda un lote desde su registro hasta su venta. Menor tiempo = mayor eficiencia operativa." />
        <KPICard label="Lotes Completados" value="47" trend={8.3} trendLabel="este mes" icon="CheckCircle" color="bg-purple-50 text-purple-600" tooltip="Número total de lotes que han completado el ciclo completo de procesamiento y venta en el período actual." />
      </div>

      {/* Yield Chart */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-4" style={{ fontFamily: '"Outfit", sans-serif' }}>
          Yield Estimado vs Real por Metal
        </h3>
        <div className="w-full h-48" aria-label="Gráfico de yield estimado vs real por metal">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={yieldData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="metal" tick={{ fontSize: 11, fontFamily: '"IBM Plex Sans", sans-serif' }} />
              <YAxis tick={{ fontSize: 11, fontFamily: '"JetBrains Mono", monospace' }} domain={[60, 100]} />
              <Tooltip
                contentStyle={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                formatter={(v) => [`${v}%`]}
              />
              <Legend wrapperStyle={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '12px' }} />
              <Bar dataKey="estimado" name="Estimado" fill="#94a3b8" radius={[3, 3, 0, 0]} />
              <Bar dataKey="real" name="Real" fill="#ea580c" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Margin Chart */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-4" style={{ fontFamily: '"Outfit", sans-serif' }}>
          Evolución de Márgenes (últimos 6 meses)
        </h3>
        <div className="w-full h-48" aria-label="Gráfico de evolución de márgenes últimos 6 meses">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={marginData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="mes" tick={{ fontSize: 11, fontFamily: '"IBM Plex Sans", sans-serif' }} />
              <YAxis tick={{ fontSize: 11, fontFamily: '"JetBrains Mono", monospace' }} domain={[0, 20]} />
              <Tooltip
                contentStyle={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                formatter={(v) => [`${v}%`]}
              />
              <Legend wrapperStyle={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '12px' }} />
              <Bar dataKey="estimado" name="Estimado" fill="#94a3b8" radius={[3, 3, 0, 0]} />
              <Bar dataKey="real" name="Real" fill="#059669" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}